import type { User } from "@assetwatch/auth";
import { z } from "zod";
import { postMlApi } from "../../api/mlApi";
import config from "../../config";
import {
  assertCanAccessMlFrontend,
  type MlFrontendAccessFlags,
} from "../../lib/access";

/**
 * The MP Graphs page's own ML API endpoint. Every operation this page needs is a
 * `meth` on this one path — adding data means adding a `meth` here, never
 * calling another page's endpoint. See `config.mlApi.frontendPath`.
 */
const MP_GRAPHS_PATH = `${config.mlApi.frontendPath}/mp_graphs`;

// The ML API returns numeric values as strings in places (e.g. "0.03569").
// Coerce them to numbers, but keep empty/absent/null as null rather than a
// coerced 0 — a gap in a series must stay a gap, not become a reading of zero.
const nullableNumber = z
  .preprocess((value) => {
    if (value === "" || value === null || value === undefined) {
      return null;
    }
    if (typeof value === "string") {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return value;
  }, z.number().nullable())
  .catch(null);

/**
 * One entry in the graph catalog. A "graph" is a whole chart, not a column:
 * `rms_ts` is the RMS chart with its X/Y/Z series and its unit already decided
 * by the backend.
 *
 * Each metric exists twice, suffixed by the store it reads from — `rms_athena`
 * and `rms_ts` are the same numbers from Athena (cheaper over long windows) and
 * Timestream (fresher).
 *
 * `y_units` can legitimately be an empty string (crest factor is dimensionless),
 * which callers must render as no unit rather than as `""`.
 */
const mpGraphTypeSchema = z.object({
  key: z.string(),
  title: z.string(),
  y_units: z.string().catch(""),
  source: z.string().catch(""),
  series: z.array(z.string()).catch([]),
});

export type MpGraphTypeInfo = z.infer<typeof mpGraphTypeSchema>;

const graphTypesResponseSchema = z.object({
  graph_types: z.array(mpGraphTypeSchema),
});

/**
 * The graph catalog. Reads no data, so it's free and safe to call on page load.
 *
 * The picker is built from this rather than a hardcoded list so new graphs (RPM
 * is next) appear without a frontend change.
 */
export async function fetchMpGraphTypes({
  user,
  flags,
}: {
  user: User | null;
  flags: MlFrontendAccessFlags;
}): Promise<MpGraphTypeInfo[]> {
  assertCanAccessMlFrontend({ flags, user });

  const payload = await postMlApi(MP_GRAPHS_PATH, {
    meth: "get_mp_graph_types",
  });

  return graphTypesResponseSchema.parse(payload).graph_types;
}

/**
 * One plotted line. `values` is index-aligned with the graph's `timestamps` —
 * same length, with `null` for a gap rather than a missing entry, which feeds
 * straight into the chart as a break in the line.
 *
 * `label` is the legend entry, used verbatim. `column` is the underlying schema
 * name (e.g. `x_rms`); the spec calls it ignorable, but it's a stable, unique,
 * axis-prefixed id, which is exactly what the legend's URL state keys off.
 */
const mpGraphSeriesSchema = z.object({
  label: z.string(),
  column: z.string().catch(""),
  values: z.array(nullableNumber),
});

export type MpGraphSeries = z.infer<typeof mpGraphSeriesSchema>;

/**
 * A state change on a graph that carries them (currently `on_off_athena`).
 *
 * Markers are self-describing — `shape` and `color` arrive on each one, so there
 * is no catalog to fetch and no vocabulary to hardcode. An unfamiliar `color`
 * falls back to a neutral rendering rather than dropping the marker.
 *
 * These mark *transitions*, not every point: the first plotted reading names the
 * starting state and after that only changes get a marker, so a machine that ran
 * the whole window returns exactly one. That makes the run between consecutive
 * markers the meaningful unit — see `buildStateRuns` in `lib/mpGraphs`.
 *
 * `timestamp` is guaranteed server-side to be a member of the graph's
 * `timestamps` (markers are computed from the same downsampled points), so no
 * nearest-neighbour snapping is needed here.
 */
const mpGraphMarkerSchema = z.object({
  timestamp: z.coerce.number(),
  label: z.string(),
  shape: z.string().catch(""),
  color: z.string().catch(""),
  note: z.string().nullish().catch(null),
});

export type MpGraphMarker = z.infer<typeof mpGraphMarkerSchema>;

/**
 * A whole chart's worth of data, columnar. `timestamps` is epoch seconds
 * ascending, and is identical across graphs from the same source, so those
 * charts share a zoom/brush grid without resampling.
 *
 * Each graph is capped at 15,000 points — the most recent 5,000 at full
 * resolution, older ones evenly downsampled — so wide windows are not uniformly
 * spaced in time. Always plot against the timestamp, never the index.
 */
const mpGraphSchema = z.object({
  key: z.string(),
  title: z.string(),
  y_units: z.string().catch(""),
  source: z.string().catch(""),
  timestamps: z.array(z.coerce.number()),
  series: z.array(mpGraphSeriesSchema),
  // Both fields are omitted entirely on graphs without markers, so they default
  // rather than being tolerated as null.
  markers: z.array(mpGraphMarkerSchema).nullish().catch(null),
  markers_truncated: z.boolean().nullish().catch(null),
});

export type MpGraph = z.infer<typeof mpGraphSchema>;

/**
 * What the query scanned, reported alongside the data.
 *
 * This is a scan-cost estimate, not a bill: it covers data scanned, not compute.
 * `usd`/`mb_scanned` are nullable — null means the backend couldn't determine
 * the figure, which has to render as "unavailable" rather than as $0.
 */
const mpQueryCostSchema = z.object({
  usd: nullableNumber,
  mb_scanned: nullableNumber,
  /** How many underlying queries the source-batching needed. */
  queries: nullableNumber,
  rows: nullableNumber,
  seconds: nullableNumber,
});

export type MpQueryCost = z.infer<typeof mpQueryCostSchema>;

const vibrationGraphsResponseSchema = z.object({
  graphs: z.array(mpGraphSchema),
  // Tolerated as absent so a backend that stops reporting cost doesn't blank the
  // charts. Absent reads as "unknown", never as free.
  cost: mpQueryCostSchema.nullish().catch(null),
});

export type MpVibrationGraphsResult = {
  graphs: MpGraph[];
  cost: MpQueryCost | null;
};

export type FetchMpVibrationGraphsInput = {
  user: User | null;
  flags: MlFrontendAccessFlags;
  monitoringPointId: string;
  timeRangeDays: number;
  /** Catalog keys (e.g. `rms_ts`). Must be non-empty. */
  graphs: string[];
};

export async function fetchMpVibrationGraphs({
  user,
  flags,
  monitoringPointId,
  timeRangeDays,
  graphs,
}: FetchMpVibrationGraphsInput): Promise<MpVibrationGraphsResult> {
  // Defense-in-depth: the same access gate the catalog call uses, before the
  // request is signed/sent. The route is also flag-gated + role-gated upstream.
  assertCanAccessMlFrontend({ flags, user });

  // One request covers every chart on the page: the backend groups the requested
  // keys by source and issues one query per source, so splitting these across
  // several HTTP calls would cost more, not less.
  const payload = await postMlApi(MP_GRAPHS_PATH, {
    meth: "get_vibration_graphs",
    external_monitoringpoint_id: monitoringPointId,
    days_back: timeRangeDays,
    graphs,
  });

  const parsed = vibrationGraphsResponseSchema.parse(payload);

  return { graphs: parsed.graphs, cost: parsed.cost ?? null };
}
